import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BaseChartDirective } from 'ng2-charts';
import { Chart, registerables, ChartConfiguration } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import zoomPlugin from 'chartjs-plugin-zoom';
import html2canvas from 'html2canvas';
import { Store } from '@ngrx/store';
import { Subject, of } from 'rxjs';
import { switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { AppState } from '../../../../../../state/app.state';
import { loadSidenavData } from '../../../../../../state/action';
import { selectSidenavDataByTabIdAndKey } from '../../../../../../state/selectors';
import {
  IInnovationMetricsHistoryPoint,
  IKeywords,
} from '../../../../../../core/models/results.model';
import { GeneralInnovationActivityResultsService } from '../../../../services/general-innovation-activity-results.service';
import { SIGNAL_COLORS } from '../../../../../../shared/utils/bracket.utils';
import {
  FONT_FAMILY, CHART_COLOR, TimeRange, axisLabel, periodLabel, filterQuarterly,
  whiteBackgroundPlugin, chartAreaBgPlugin, categoryXScale, baseTooltip, categoryZoom, COMMON_CHART_OPTIONS,
} from '../../../../../../shared/utils/chart-style';

export type ScoreSecondaryAxis = 'size' | 'growth' | 'quality' | 'rank' | 'abs_growth' | null;

interface ScoreAxisCfg {
  label: string;
  field: keyof IInnovationMetricsHistoryPoint;
  color: string;
  type: 'scatter' | 'bar';
  hoverSuffix?: string;
}

const SCORE_SECONDARY_AXIS_CONFIG: Record<Exclude<ScoreSecondaryAxis, null>, ScoreAxisCfg> = {
  abs_growth: { label: 'Abs. Growth (12m)', field: 'absolute_growth_12m', color: '#CADB8F', type: 'bar', hoverSuffix: ' Inventions' },
  growth: { label: 'Rel. Growth (12m)', field: 'relative_growth_12m', color: '#F6D08F', type: 'bar', hoverSuffix: '%' },
  size: { label: 'Size (10y)', field: 'invention_count_120m', color: '#9EDEB8', type: 'bar' },
  quality: { label: 'Quality', field: 'quality_score_12m', color: '#a855f7', type: 'scatter' },
  rank: { label: 'Percentile Rank', field: 'percentile_rank', color: '#0f766e', type: 'scatter' },
};

Chart.register(...registerables, zoomPlugin, annotationPlugin);

@Component({
  selector: 'app-innovation-activity-over-time',
  standalone: true,
  imports: [CommonModule, MatTooltipModule, BaseChartDirective],
  templateUrl: './innovation-activity-over-time.component.html',
  styleUrl: './innovation-activity-over-time.component.css',
})
export class InnovationActivityOverTimeComponent implements OnChanges, OnDestroy {
  @Input() tabId!: string;
  @Input() entityids: string[] | null = null;
  @Input() tab_name: string | null = null;
  @Input() query: string | null = null;
  @Input() printMode = false;             // PDF report: lock axis, hide controls
  @Input() lockedAxis?: ScoreSecondaryAxis;

  @ViewChild(BaseChartDirective) private chartDirective?: BaseChartDirective;
  @ViewChild('exportTarget') private exportTarget?: ElementRef<HTMLDivElement>;

  private destroy$ = new Subject<void>();

  history: IInnovationMetricsHistoryPoint[] = [];

  chartjsData!: ChartConfiguration<'bar'>['data'];
  chartjsOptions!: ChartConfiguration<'bar'>['options'];
  readonly chartPlugins = [whiteBackgroundPlugin, chartAreaBgPlugin];

  showScoreChart = false;
  showScoreSpinner = false;
  scoreLoadingFailed = false;
  scoreDataMissing = false;

  secondaryAxis: ScoreSecondaryAxis = 'abs_growth';
  timeRange: TimeRange = '10y';

  readonly SIGNAL = SIGNAL_COLORS;
  readonly SECONDARY_CFG = SCORE_SECONDARY_AXIS_CONFIG;

  keywords: IKeywords[] = [];
  keywordsLoading = false;
  keywordsError = false;
  showKeywordsNoData = false;

  constructor(
    private store: Store<AppState>,
    private apiService: GeneralInnovationActivityResultsService,
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.printMode && this.lockedAxis !== undefined) {
      this.secondaryAxis = this.lockedAxis;
    }
    if (changes['tabId'] || changes['entityids'] || changes['query']) {
      this.reset();
      this.loadHistory();
      // this.loadKeywords();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private reset(): void {
    this.history = [];
    this.showScoreChart = false;
    this.showScoreSpinner = false;
    this.scoreLoadingFailed = false;
    this.scoreDataMissing = false;
  }

  get scoreChartTitle(): string {
    return this.secondaryAxis
      ? `Innovation Score vs ${SCORE_SECONDARY_AXIS_CONFIG[this.secondaryAxis].label}`
      : 'Innovation Score';
  }

  toggleScoreSecondaryAxis(axis: Exclude<ScoreSecondaryAxis, null>): void {
    this.secondaryAxis = this.secondaryAxis === axis ? null : axis;
    if (this.history.length) this.buildScoreChart();
  }

  setTimeRange(range: TimeRange): void {
    this.timeRange = range;
    if (this.history.length) this.buildScoreChart();
  }

  resetZoom(): void {
    this.chartDirective?.chart?.resetZoom();
  }

  /** Exact chart image (Chart.js native export) for the server-rendered PDF report. */
  getChartImage(scale = 2): string | null {
    return this.chartDirective?.chart?.toBase64Image('image/png', scale) ?? null;
  }

  async downloadPng(): Promise<void> {
    const node = this.exportTarget?.nativeElement;
    if (!node) return;
    const canvas = await html2canvas(node, {
      backgroundColor: '#ffffff',
      scale: 2,
      useCORS: true,
    });
    const safeName = (this.tab_name || 'innovation-activity').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = `${safeName}-innovation-activity.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  private getFilteredHistory(): IInnovationMetricsHistoryPoint[] {
    return filterQuarterly(this.history, this.timeRange);
  }

  private loadHistory(): void {
    const entityid = this.entityids?.[0];
    if (!entityid || !this.tabId) {
      this.scoreDataMissing = true;
      this.showScoreChart = false;
      return;
    }

    // TODO: remove dummy short-circuit and restore backend call below.
    // this.history = generateDummyInnovationHistory(entityid);
    // this.buildScoreChart();
    // this.showScoreSpinner = false;
    // this.scoreLoadingFailed = false;
    // this.scoreDataMissing = false;
    // return;

    const currentTabId = this.tabId;
    this.showScoreSpinner = true;
    this.scoreLoadingFailed = false;
    this.scoreDataMissing = false;

    this.store.select(selectSidenavDataByTabIdAndKey(currentTabId, 'innov_metrics_history')).pipe(
      take(1),
      switchMap((cached) => {
        if (cached) {
          if (this.tabId === currentTabId) {
            this.history = cached;
            this.buildScoreChart();
            this.showScoreSpinner = false;
          }
          return of(null);
        }
        return this.apiService.getInnovationHistory(entityid!, currentTabId).pipe(
          takeUntil(this.destroy$),
          tap((res) => {
            const payload = res.body?.payload;
            if (!res.ok || !payload?.history?.length) {
              if (this.tabId === currentTabId) {
                this.showScoreSpinner = false;
                this.scoreDataMissing = true;
                this.showScoreChart = false;
              }
              return;
            }
            const targetTabId = payload.tabId ?? currentTabId;
            const historyData = payload.history;
            this.store.dispatch(loadSidenavData({ tabId: targetTabId, sidenavKey: 'innov_metrics_history', data: historyData }));
            if (this.tabId === currentTabId && targetTabId === currentTabId) {
              this.showScoreSpinner = false;
              this.history = historyData;
              this.buildScoreChart();
            }
          }),
        );
      }),
    ).subscribe({
      error: () => {
        if (this.tabId === currentTabId) {
          this.showScoreSpinner = false;
          this.scoreLoadingFailed = true;
          this.showScoreChart = false;
        }
      },
    });
  }

  private buildScoreChart(): void {
    const filtered = this.getFilteredHistory();
    if (!filtered.length) {
      this.scoreDataMissing = true;
      this.showScoreChart = false;
      return;
    }

    const labels = filtered.map(p => axisLabel(p.date_eom));
    const scoreData = filtered.map(p => p.innovation_score);

    const datasets: any[] = [];

    if (this.secondaryAxis) {
      const cfg = SCORE_SECONDARY_AXIS_CONFIG[this.secondaryAxis];
      const secData = filtered.map(p => (p[cfg.field] as number | null) ?? null);

      if (cfg.type === 'bar') {
        datasets.push({
          type: 'bar',
          label: cfg.label,
          data: secData,
          yAxisID: 'y2',
          backgroundColor: cfg.color,
          borderColor: cfg.color,
          borderWidth: 0,
          borderRadius: 4,
          borderSkipped: false,
          categoryPercentage: 0.85,
          barPercentage: 0.7,
          order: 2,
        });
      } else {
        datasets.push({
          type: 'line',
          label: cfg.label,
          data: secData,
          yAxisID: 'y2',
          borderColor: cfg.color,
          borderWidth: 2,
          tension: 0.35,
          cubicInterpolationMode: 'monotone',
          pointRadius: 2,
          pointHoverRadius: 4,
          pointBackgroundColor: cfg.color,
          fill: false,
          order: 2,
        });
      }
    }

    datasets.push({
      type: 'line',
      label: 'Innovation Score',
      data: scoreData,
      yAxisID: 'y',
      borderColor: CHART_COLOR.scoreLine,
      borderWidth: 2,
      tension: 0.35,
      cubicInterpolationMode: 'monotone',
      pointRadius: 1.5,
      pointHoverRadius: 4,
      pointBackgroundColor: CHART_COLOR.scoreLine,
      fill: 'false',
      order: 1,
    });

    this.chartjsData = { labels, datasets } as any;

    const yScale: any = {
      position: 'left',
      min: 0,
      max: 100,
      title: {
        display: true,
        text: 'Innovation Score',
        font: { family: FONT_FAMILY, size: 14, weight: 500 },
        color: CHART_COLOR.axisTitle,
        padding: { bottom: 8 },
      },
      // Base ticks every 10; inject the 25/75 signal thresholds on top.
      afterBuildTicks: (scale: any) => {
        scale.ticks = [...scale.ticks, { value: 25 }, { value: 75 }].sort((a: any, b: any) => a.value - b.value);
      },
      ticks: {
        font: { family: FONT_FAMILY, size: 14 },
        color: CHART_COLOR.axisText,
        padding: 8,
        stepSize: 10,
        callback: (value: number | string) => {
          const v = typeof value === 'number' ? value : Number(value);
          if (v === 25) return 'SELL · 25';
          if (v === 75) return 'BUY · 75';
          return `${v}`;
        },
      },
      // grid: { color: CHART_COLOR.grid, z: -1 },   // z:-1 → gridlines behind the bars (always on)
      grid: { display: this.printMode, color: CHART_COLOR.grid, z: -1 },   // print-only gridlines
      border: { display: false },
    };

    const scales: any = { x: categoryXScale(), y: yScale };
    scales.x.grid.display = this.printMode;   // print-only gridlines

    if (this.secondaryAxis) {
      const cfg = SCORE_SECONDARY_AXIS_CONFIG[this.secondaryAxis];
      const isBounded = this.secondaryAxis === 'quality' || this.secondaryAxis === 'rank';
      scales.y2 = {
        position: 'right',
        min: isBounded ? 0 : undefined,
        max: isBounded ? 100 : undefined,
        title: {
          display: true,
          text: cfg.label,
          font: { family: FONT_FAMILY, size: 14, weight: 500 },
          color: CHART_COLOR.axisTitle,   // neutral dark text (series color kept on trace only)
          padding: { bottom: 8 },
        },
        ticks: {
          count: 11,   // align to the left axis' 10-step gridlines
          font: { family: FONT_FAMILY, size: 11 },
          color: CHART_COLOR.axisText,   // neutral dark text
          padding: 8,
          callback: (v: number | string) => Math.round(Number(v)).toLocaleString(),
        },
        grid: { display: false },
        border: { display: false },
      };
    }

    this.chartjsOptions = {
      ...COMMON_CHART_OPTIONS,
      plugins: {
        legend: { display: false },
        tooltip: baseTooltip({
          title: (items: any[]) => periodLabel(filtered[items[0].dataIndex].date_eom),
          label: (item: any) => {
            const v = item.parsed.y;
            if (v == null) return '';
            const dsLabel = item.dataset.label ?? '';
            if (dsLabel === 'Innovation Score') {
              return ` ${dsLabel}: ${Number(v).toFixed(1)}`;
            }
            const cfg = this.secondaryAxis ? SCORE_SECONDARY_AXIS_CONFIG[this.secondaryAxis] : null;
            const suffix = cfg?.hoverSuffix ?? '';
            return ` ${dsLabel}: ${Math.round(v)}${suffix}`;
          },
        }),
        annotation: {
          annotations: {
            buyLine: {
              type: 'line',
              yMin: 75, yMax: 75,
              yScaleID: 'y',
              borderColor: SIGNAL_COLORS.BUY + 'b3',
              borderWidth: 1.5,
              borderDash: [4, 4],
            },
            sellLine: {
              type: 'line',
              yMin: 25, yMax: 25,
              yScaleID: 'y',
              borderColor: SIGNAL_COLORS.SELL + 'b3',
              borderWidth: 1.5,
              borderDash: [4, 4],
            },
          },
        },
        zoom: categoryZoom(),
      },
      scales,
    } as any;

    this.showScoreChart = true;
  }

  // private loadKeywords(): void {
  //   if (!this.tabId) return;

  //   this.keywordsLoading = true;
  //   this.keywordsError = false;
  //   this.showKeywordsNoData = false;

  //   this.store.select(selectSidenavDataByTabIdAndKey(this.tabId, 'top_keywords')).pipe(
  //     take(1),
  //     switchMap((cached) => {
  //       if (cached) {
  //         this.keywords = cached;
  //         this.keywordsLoading = false;
  //         this.showKeywordsNoData = !this.keywords.length;
  //         return of(null);
  //       }
  //       return this.apiService.getKeywords(this.query, this.entityids, this.tabId).pipe(
  //         takeUntil(this.destroy$),
  //         tap((res) => {
  //           this.keywordsLoading = false;
  //           if (res.status === 204 || !res.body?.payload?.results?.length) {
  //             this.keywords = [];
  //             this.showKeywordsNoData = true;
  //             return;
  //           }
  //           const results = res.body.payload.results;
  //           this.store.dispatch(loadSidenavData({ tabId: this.tabId, sidenavKey: 'top_keywords', data: results }));
  //           this.keywords = results;
  //         }),
  //       );
  //     }),
  //   ).subscribe({
  //     error: () => {
  //       this.keywordsLoading = false;
  //       this.keywordsError = true;
  //     },
  //   });
  // }
}
